import { PlayerRef } from '@remotion/player';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { MAX_AUTOSCROLL_SPEED } from './constants';
import { EditorStarterItem } from './items/item-type';
import type { TransitionItem } from './items/transition/transition-item-type';
import { getTrackHeight } from './state/items';
import { TrackType } from './state/types';
import {
  getItemLeftOffset,
  getItemRoundedPosition,
  getItemWidth,
  getOffsetOfTrack,
} from './utils/position-utils';
import { timelineScrollableContainerRef } from './utils/restore-scroll-after-zoom';
import { useTimelineContainerAutoScroll } from './utils/use-timeline-container-auto-scroll';

export interface ItemMeta {
  id: string;
  trackIndex: number;
  from: number;
  durationInFrames: number;
  initialOffset: { x: number; y: number };
  height: number;
}

/** 트랜지션 드래그 오버레이 메타 정보 */
export interface TransitionMeta {
  id: string;
  transition: TransitionItem;
  initialOffset: { x: number; y: number };
  trackHeight: number;
}

interface DragOverlayState {
  isDragging: boolean;
  draggedItemIds: string[];
  itemsMeta: ItemMeta[];
  transitionsMeta: TransitionMeta[];
  tracks: TrackType[] | null;
  cursorPosition: { x: number; y: number } | null;
  timelineWidth: number;
  visibleFrames: number;
  playerRef: React.RefObject<PlayerRef | null> | null;
  snappedPositions: Record<string, number> | null; // itemId -> snapped frame position
}

interface StartDragParams {
  itemIds: string[];
  clickedItemId: string;
  timelineWidth: number;
  visibleFrames: number;
  clickX: number;
  clickY: number;
  tracks: TrackType[];
  items: Record<string, EditorStarterItem>;
  /** 함께 드래그할 트랜지션 ID 배열 */
  transitionIds?: string[];
  /** 트랜지션 데이터 */
  transitions?: Record<string, TransitionItem>;
}

interface DragOverlayContextValue extends DragOverlayState {
  startDrag: (params: StartDragParams) => void;
  updateCursorPosition: (x: number, y: number) => void;
  stopDrag: () => void;
  setSnappedPositions: (positions: Record<string, number> | null) => void;
}

const DragOverlayContext = createContext<DragOverlayContextValue | null>(null);

export const useDragOverlay = () => {
  const context = useContext(DragOverlayContext);
  if (!context) {
    throw new Error('useDragOverlay must be used within a DragOverlayProvider');
  }
  return context;
};

interface DragOverlayProviderProps {
  children: React.ReactNode;
  playerRef: React.RefObject<PlayerRef | null>;
}

const isSamePositions = ({
  positions1,
  positions2,
}: {
  positions1: Record<string, number> | null;
  positions2: Record<string, number> | null;
}) => {
  if (positions1 === positions2) {
    return true;
  }
  if (positions1 === null || positions2 === null) {
    return false;
  }
  if (Object.keys(positions1).length !== Object.keys(positions2).length) {
    return false;
  }
  for (const key of Object.keys(positions1)) {
    if (positions1[key] !== positions2[key]) {
      return false;
    }
  }
  return true;
};

export const DragOverlayProvider: React.FC<DragOverlayProviderProps> = ({
  children,
  playerRef,
}) => {
  const [state, setState] = useState<DragOverlayState>({
    isDragging: false,
    draggedItemIds: [],
    itemsMeta: [],
    transitionsMeta: [],
    cursorPosition: null,
    timelineWidth: 0,
    visibleFrames: 0,
    playerRef: playerRef || null,
    tracks: [],
    snappedPositions: null,
  });

  useTimelineContainerAutoScroll({
    isDragging: state.isDragging,
    edgeThreshold: 50,
    maxScrollSpeed: MAX_AUTOSCROLL_SPEED,
    xAxis: true,
    yAxis: true,
  });

  const startDrag = useCallback(
    (params: StartDragParams) => {
      const {
        itemIds,
        clickedItemId,
        timelineWidth,
        visibleFrames,
        clickX,
        clickY,
        tracks,
        items,
        transitionIds = [],
        transitions = {},
      } = params;
      // 클릭된 item의 track index 찾기
      let clickedTrackIndex = -1;
      for (let i = 0; i < tracks.length; i++) {
        const curTracks = tracks[i];

        if (curTracks?.items.some((item) => item === clickedItemId)) {
          clickedTrackIndex = i;
          break;
        }
      }

      if (clickedTrackIndex === -1) {
        throw new Error('Clicked item not found in tracks');
      }

      // 기존 ref를 사용하여 timeline container 가져오기
      const containerRect =
        timelineScrollableContainerRef.current?.getBoundingClientRect();

      if (!containerRect) {
        return;
      }

      // 각 드래그된 item에 대한 metadata 구성
      const itemsMeta: ItemMeta[] = [];

      for (const itemId of itemIds) {
        // item과 해당 track 찾기
        let foundTrackIndex = -1;
        let foundItem: string | null = null;

        for (let i = 0; i < tracks.length; i++) {
          const item = tracks[i]?.items.find((it) => it === itemId);
          if (item) {
            foundTrackIndex = i;
            foundItem = item;
            break;
          }
        }

        if (!foundItem || foundTrackIndex === -1) {
          continue;
        }

        const rawItemLeft = getItemLeftOffset({
          timelineWidth,
          totalDurationInFrames: visibleFrames,
          from: items[foundItem]?.from || 0,
        });

        const { roundedLeft: itemLeft } = getItemRoundedPosition(
          rawItemLeft,
          getItemWidth({
            itemDurationInFrames: items[foundItem]?.durationInFrames || 0,
            timelineWidth,
            totalDurationInFrames: visibleFrames,
          }),
        );

        // timeline item의 절대 위치를 계산
        const itemTop =
          containerRect.top +
          getOffsetOfTrack({ trackIndex: foundTrackIndex, tracks, items });
        const itemAbsoluteLeft = containerRect.left + itemLeft;

        // click 위치에서 item의 왼쪽 위 모서리까지의 offset을 계산
        const offsetX = clickX - itemAbsoluteLeft;
        const offsetY = clickY - itemTop;

        const height = getTrackHeight({
          track: tracks[foundTrackIndex],
          items,
        });

        const item = items[foundItem];

        if (item) {
          itemsMeta.push({
            id: itemId,
            trackIndex: foundTrackIndex,
            from: item.from,
            durationInFrames: item.durationInFrames,
            initialOffset: { x: offsetX, y: offsetY },
            height,
          });
        }
      }

      // 트랜지션 메타 정보 구성
      const transitionsMeta: TransitionMeta[] = [];

      for (const transitionId of transitionIds) {
        const transition = transitions[transitionId];
        if (!transition) continue;

        // 트랜지션이 속한 트랙 찾기
        const trackIndex = tracks.findIndex((t) => t.id === transition.trackId);
        if (trackIndex === -1) continue;

        const rawTransitionLeft = getItemLeftOffset({
          timelineWidth,
          totalDurationInFrames: visibleFrames,
          from: transition.from,
        });

        const { roundedLeft: transitionLeft } = getItemRoundedPosition(
          rawTransitionLeft,
          getItemWidth({
            itemDurationInFrames: transition.durationInFrames,
            timelineWidth,
            totalDurationInFrames: visibleFrames,
          }),
        );

        // 트랜지션의 절대 위치 계산
        const transitionTop =
          containerRect.top + getOffsetOfTrack({ trackIndex, tracks, items });
        const transitionAbsoluteLeft = containerRect.left + transitionLeft;

        // click 위치에서 트랜지션의 왼쪽 위 모서리까지의 offset
        const offsetX = clickX - transitionAbsoluteLeft;
        const offsetY = clickY - transitionTop;

        const trackHeight = getTrackHeight({
          track: tracks[trackIndex],
          items,
        });

        transitionsMeta.push({
          id: transitionId,
          transition,
          initialOffset: { x: offsetX, y: offsetY },
          trackHeight,
        });
      }

      setState({
        isDragging: true,
        draggedItemIds: itemIds,
        itemsMeta,
        transitionsMeta,
        cursorPosition: { x: clickX, y: clickY },
        timelineWidth,
        visibleFrames,
        playerRef: playerRef || null,
        tracks,
        snappedPositions: null,
      });
    },
    [playerRef],
  );

  const updateCursorPosition = useCallback((x: number, y: number) => {
    setState((prev) => ({
      ...prev,
      cursorPosition: { x, y },
    }));
  }, []);

  const setSnappedPositions = useCallback(
    (positions: Record<string, number> | null) => {
      setState((prev) => {
        if (
          isSamePositions({
            positions1: positions,
            positions2: prev.snappedPositions,
          })
        ) {
          return prev;
        }

        return {
          ...prev,
          snappedPositions: positions,
        };
      });
    },
    [],
  );

  const stopDrag = useCallback(() => {
    setState({
      isDragging: false,
      draggedItemIds: [],
      itemsMeta: [],
      transitionsMeta: [],
      cursorPosition: null,
      timelineWidth: 0,
      visibleFrames: 0,
      playerRef: null,
      tracks: null,
      snappedPositions: null,
    });
  }, []);

  const contextValue = useMemo(
    () => ({
      ...state,
      startDrag,
      updateCursorPosition,
      stopDrag,
      setSnappedPositions,
    }),
    [state, startDrag, updateCursorPosition, stopDrag, setSnappedPositions],
  );

  return (
    <DragOverlayContext.Provider value={contextValue}>
      {children}
    </DragOverlayContext.Provider>
  );
};
